import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CircularProgress from '@mui/material/CircularProgress';
import { sa, badgeStyle, usageFill } from './tokens';
import { formatPercent } from '../../utils/creditFormat';

// ---------------------------------------------------------------------------
// Typography / section headers
// ---------------------------------------------------------------------------

export const PageTitle = styled.h1`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 22px;
  color: ${sa.color.text};
  margin: 0;
`;

export const SectionTitle = styled.h2`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: ${(p) => (p.$size === 'lg' ? '20px' : '18px')};
  color: ${sa.color.text};
  margin: 0;
`;

export const SectionSubtitle = styled.p`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.regular};
  font-size: 13px;
  color: ${sa.color.textGray};
  margin: 6px 0 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: ${(p) => (p.$align === 'end' ? 'flex-end' : 'center')};
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%;
  margin-bottom: 12px;
`;
const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

// Title (+ optional subtitle) on the left, controls on the right — the recurring section head.
export function SectionHeaderRow({ title, subtitle, size, align, children }) {
  return (
    <HeaderRow $align={align}>
      <div>
        <SectionTitle $size={size}>{title}</SectionTitle>
        {subtitle && <SectionSubtitle>{subtitle}</SectionSubtitle>}
      </div>
      {children && <HeaderControls>{children}</HeaderControls>}
    </HeaderRow>
  );
}
SectionHeaderRow.propTypes = {
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  size: PropTypes.oneOf(['lg', 'md']),
  align: PropTypes.oneOf(['center', 'end']),
  children: PropTypes.node,
};

// ---------------------------------------------------------------------------
// Type badge (GROUP / SCHOOL)
// ---------------------------------------------------------------------------

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: ${sa.radius.sm};
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 11px;
  line-height: 1;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
`;

export function TypeBadge({ type }) {
  if (!type) return null;
  const { bg, fg } = badgeStyle(type);
  return <Badge $bg={bg} $fg={fg}>{String(type).toUpperCase()}</Badge>;
}
TypeBadge.propTypes = { type: PropTypes.string };

// ---------------------------------------------------------------------------
// Progress bar (usage %)
// ---------------------------------------------------------------------------

const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;
const Track = styled.div`
  flex: 1 0 0;
  min-width: 0;
  height: 8px;
  background: ${sa.color.mist};
  border-radius: ${sa.radius.sm};
  overflow: hidden;
`;
const Fill = styled.div`
  height: 100%;
  border-radius: ${sa.radius.sm};
  background: ${(p) => p.$color};
  width: ${(p) => p.$pct}%;
`;
const PctLabel = styled.span`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: ${(p) => (p.$small ? '11px' : '12px')};
  color: ${sa.color.text};
  width: ${(p) => (p.$small ? '30px' : '40px')};
  text-align: left;
  flex-shrink: 0;
`;

// The bar width is clamped to 0..100, but the LABEL keeps the grain the credits API reports
// (`pct` comes back to two places, e.g. 6.02). Rounding the label collapsed small consumption
// to a flat "0%" and read 6.02% as "6%".
export function ProgressBar({ pct, showLabel, small }) {
  const value = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <ProgressRow>
      <Track>
        <Fill $pct={value} $color={usageFill(value)} />
      </Track>
      {showLabel && <PctLabel $small={small}>{formatPercent(value, { decimals: 2 })}</PctLabel>}
    </ProgressRow>
  );
}
ProgressBar.propTypes = {
  pct: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showLabel: PropTypes.bool,
  small: PropTypes.bool,
};
ProgressBar.defaultProps = { showLabel: true };

// ---------------------------------------------------------------------------
// KPI metrics bar
// ---------------------------------------------------------------------------

const KPI_ACCENTS = [sa.color.accentBlue, sa.color.accentOrange, sa.color.accentAmber, sa.color.accentGreen];

const KpiWrap = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  background: ${sa.color.white};
  border: 1px solid ${sa.color.strokeLight};
  border-radius: ${sa.radius.lg};
  padding: 16px 0;
`;
const Stat = styled.div`
  flex: 1 0 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 24px;
`;
const StatLabel = styled.p`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 12px;
  color: ${sa.color.kpiLabel};
  text-transform: uppercase;
  margin: 0;
`;
const StatValue = styled.p`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.extrabold};
  font-size: 40px;
  line-height: 1.1;
  color: ${sa.color.kpiValue};
  margin: 0;
`;
const StatAccent = styled.div`
  height: 4px;
  border-radius: 2px;
  width: 100%;
  background: ${(p) => p.$color};
`;
const KpiDivider = styled.div`
  width: 1px;
  height: 90px;
  background: ${sa.color.strokeLight};
  flex-shrink: 0;
`;

// stats: [{ label, value }] — accent colour is positional, matching the design.
export function KpiBar({ stats }) {
  return (
    <KpiWrap>
      {stats.map((stat, i) => (
        <React.Fragment key={stat.label}>
          {i > 0 && <KpiDivider />}
          <Stat>
            <StatLabel>{stat.label}</StatLabel>
            <StatValue>{stat.value}</StatValue>
            <StatAccent $color={KPI_ACCENTS[i % KPI_ACCENTS.length]} />
          </Stat>
        </React.Fragment>
      ))}
    </KpiWrap>
  );
}
KpiBar.propTypes = {
  stats: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.node })
  ).isRequired,
};

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------

export const PillButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid ${(p) => (p.$variant === 'outline' ? sa.color.stroke : 'transparent')};
  cursor: pointer;
  font-family: ${sa.font.family};
  font-weight: ${sa.font.medium};
  font-size: ${(p) => (p.$size === 'lg' ? '13px' : '12px')};
  padding: ${(p) => (p.$size === 'lg' ? '8px 12px' : '6px 12px')};
  border-radius: ${sa.radius.sm};
  white-space: nowrap;
  flex-shrink: 0;
  transition: background-color 0.15s ease-in-out, opacity 0.15s;
  background: ${(p) =>
    p.$variant === 'outline'
      ? sa.color.white
      : p.$variant === 'danger'
      ? sa.color.pink
      : p.$variant === 'purple'
      ? sa.color.purple
      : p.$variant === 'blue'
      ? sa.color.accentBlue
      : p.$variant === 'green'
      ? sa.color.accentGreen
      : p.$variant === 'orange'
      ? sa.color.accentOrange
      : p.$variant === 'mist'
      ? sa.color.blue
      : sa.color.teal};
  color: ${(p) => (p.$variant === 'outline' ? sa.color.text : sa.color.white)};

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

// ---------------------------------------------------------------------------
// Credit / Cost toggle (pill)
// ---------------------------------------------------------------------------

const ToggleTrack = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  background: ${sa.color.mist};
  border-radius: ${sa.radius.pill};
`;
const ToggleSeg = styled.button`
  border: none;
  cursor: pointer;
  font-family: ${sa.font.family};
  font-size: 13px;
  padding: 6px 12px;
  border-radius: ${sa.radius.chip};
  background: ${(p) => (p.$active ? sa.color.white : 'transparent')};
  color: ${(p) => (p.$active ? sa.color.text : sa.color.textGray)};
  font-weight: ${(p) => (p.$active ? sa.font.bold : sa.font.medium)};
`;

// Controlled pill toggle. Pass value ('credit'|'cost') + onChange, or wire to CreditModeContext
// at the call site.
export function CreditCostToggle({ value, onChange }) {
  return (
    <ToggleTrack role='group' aria-label='Credit or cost'>
      <ToggleSeg type='button' $active={value === 'credit'} onClick={() => onChange('credit')}>
        Credit
      </ToggleSeg>
      <ToggleSeg type='button' $active={value === 'cost'} onClick={() => onChange('cost')}>
        Cost
      </ToggleSeg>
    </ToggleTrack>
  );
}
CreditCostToggle.propTypes = {
  value: PropTypes.oneOf(['credit', 'cost']).isRequired,
  onChange: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
// Select pill (month / sort dropdowns)
// ---------------------------------------------------------------------------

const SelectShell = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 100%;

  select {
    appearance: none;
    -webkit-appearance: none;
    background: ${sa.color.white};
    border: 1px solid ${sa.color.stroke};
    border-radius: ${sa.radius.sm};
    font-family: ${sa.font.family};
    font-size: ${(p) => (p.$size === 'sm' ? '12px' : '13px')};
    font-weight: ${sa.font.semibold};
    color: ${sa.color.text};
    padding: ${(p) => (p.$size === 'sm' ? '6px 34px 6px 12px' : '8px 34px 8px 16px')};
    cursor: pointer;
    line-height: normal;
    width: 100%;
  }
  svg {
    position: absolute;
    right: 10px;
    pointer-events: none;
    font-size: 16px;
    color: ${sa.color.text};
  }
`;

export function SelectPill({ value, onChange, options, size, ariaLabel, testId }) {
  return (
    <SelectShell $size={size}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
        data-testid={testId}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}        
      </select>
      <KeyboardArrowDownIcon />
    </SelectShell>
  );
}
SelectPill.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.any, label: PropTypes.node })
  ).isRequired,
  size: PropTypes.oneOf(['sm', 'md']),
  ariaLabel: PropTypes.string,
  testId: PropTypes.string,
};
SelectPill.defaultProps = { size: 'md' };

// ---------------------------------------------------------------------------
// Card + loading/empty/error states
// ---------------------------------------------------------------------------

export const Card = styled.div`
  width: 100%;
  background: ${sa.color.white};
  border: 1px solid ${(p) => p.$border || sa.color.stroke};
  border-radius: ${(p) => p.$radius || sa.radius.lg};
  padding: ${(p) => p.$pad || '20px'};
`;

const CenterState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px 0;
  font-family: ${sa.font.family};
  font-size: 14px;
  color: ${sa.color.textGray};
`;

export function SectionStates({ loading, error, isEmpty, emptyText, onRetry }) {
  if (loading) {
    return (
      <CenterState>
        <CircularProgress size={26} sx={{ color: sa.color.teal }} />
      </CenterState>
    );
  }
  if (error) {
    return (
      <CenterState style={{ color: sa.color.pink, flexDirection: 'column', gap: 10 }}>
        <span>{error}</span>
        {onRetry && (
          <PillButton type='button' $variant='outline' onClick={onRetry}>
            Retry
          </PillButton>
        )}
      </CenterState>
    );
  }
  if (isEmpty) {
    return <CenterState>{emptyText || 'No data for this period.'}</CenterState>;
  }
  return null;
}
SectionStates.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.string,
  isEmpty: PropTypes.bool,
  emptyText: PropTypes.string,
  onRetry: PropTypes.func,
};
